import { useEffect, useState } from 'react';
import {
  fetchDepartureLanguages,
  setEntryPlayLanguage,
  setPlayLanguage,
  setPlayLanguageMode,
} from '../api/queue';
import {
  VISIBLE_CHIPS,
  optionsFor,
  type DeparturePlayLanguages,
  type PlayLanguageOption,
} from '../lib/play-languages';
import { LanguageChipLabel } from './LanguageFlag';

// -----------------------------------------------------------------------------
// LanguageChips — one row of play-language chips.
// -----------------------------------------------------------------------------
// Options are the experience's own enabled languages, popularity-ordered by the
// DB. Egypt ships 12, so only the top few are chips and the tail hides behind
// "meer…": on a phone the common pick stays one tap away and the modal never
// grows a scrollbar. The selected language is always on the visible row, even
// when popularity buries it.

export function LanguageChips({
  value,
  disabled,
  options,
  compact,
  onChange,
}: {
  value: string | null;
  disabled?: boolean;
  options: PlayLanguageOption[];
  /** Per-person rows: smaller chips, fewer of them before the expander. */
  compact?: boolean;
  onChange: (code: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const limit = compact ? 4 : VISIBLE_CHIPS;
  const tail = options.slice(limit);
  const selectedInTail = value != null && tail.some((o) => o.code === value);
  const visible = expanded || selectedInTail ? options : options.slice(0, limit);
  const hiddenCount = options.length - visible.length;

  return (
    <div
      className={`checkin-flow-lang-chips${compact ? ' checkin-flow-lang-chips-compact' : ''}`}
      role="radiogroup"
      aria-label="Speeltaal"
    >
      {visible.map((l) => {
        const active = value === l.code;
        return (
          <button
            key={l.code}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={l.name}
            title={l.name}
            className={`walkin-quick-chip checkin-flow-lang-chip ${
              active ? 'walkin-quick-chip-active' : ''
            }`}
            disabled={disabled}
            onClick={() => onChange(l.code)}
          >
            <LanguageChipLabel code={l.code} />
          </button>
        );
      })}
      {hiddenCount > 0 ? (
        <button
          type="button"
          className="btn-link checkin-flow-lang-more"
          disabled={disabled}
          onClick={() => setExpanded(true)}
        >
          meer… ({hiddenCount})
        </button>
      ) : null}
    </div>
  );
}

// -----------------------------------------------------------------------------
// PlayLanguagePicker — the check-in confirmation's language block.
// -----------------------------------------------------------------------------
// A departure is in exactly one of two modes, never a mixture (the DB enforces
// it): one language for the whole party, or one per person. The picker shows
// only the mode it is in, so the operator never has to reconcile a group row
// against individual rows that disagree.
//
//   group       chips for the party + "per persoon instellen ›"
//   per_person  a chip row per person + "‹ terug naar één taal voor iedereen"
//
// Switching to per-person seeds every person with what the party runs now, so
// the rows open telling the truth. Switching back clears the overrides.

export function PlayLanguagePicker({
  departureId,
  experienceId,
  langOptions,
  /** Prefill so the block renders its chips before the first fetch lands. */
  initialLanguage,
  initialConfirmed,
}: {
  departureId: string;
  experienceId: string | null;
  langOptions: Record<string, PlayLanguageOption[]> | null;
  initialLanguage: string | null;
  initialConfirmed: boolean;
}) {
  const [state, setState] = useState<DeparturePlayLanguages | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchDepartureLanguages(departureId)
      .then((d) => {
        if (!cancelled) setState(d);
      })
      .catch(() => {
        // Non-fatal: the group chips below still work off the prefill, and a
        // failed read must not block the operator from finishing check-in.
      });
    return () => {
      cancelled = true;
    };
  }, [departureId]);

  function flash(message: string) {
    setNote(message);
    window.setTimeout(() => setNote(null), 2500);
  }

  // Every mutation returns (or is followed by) the full departure state, so the
  // block always re-renders from the DB's idea of the mode rather than a local
  // guess that could drift from it.
  async function run(
    action: () => Promise<DeparturePlayLanguages>,
    okMessage: string,
  ) {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      setState(await action());
      flash(okMessage);
    } catch (e) {
      setError(`Taal opslaan mislukt: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  const mode = state?.play_language_mode ?? 'group';
  const groupValue =
    state?.play_language ?? state?.play_language_effective ?? initialLanguage;
  const confirmed = state?.play_language_confirmed ?? initialConfirmed;
  const options = optionsFor(langOptions, experienceId, groupValue);

  return (
    <div className="checkin-flow-lang">
      <div className="checkin-flow-lang-head">
        <span className="checkin-flow-lang-label">Speeltaal</span>
        <span className="checkin-flow-lang-mode muted">
          {mode === 'group' ? '· iedereen' : '· per persoon'}
        </span>
      </div>

      {mode === 'group' ? (
        <>
          <LanguageChips
            value={groupValue}
            disabled={busy}
            options={options}
            onChange={(code) =>
              void run(async () => {
                await setPlayLanguage(departureId, code);
                return fetchDepartureLanguages(departureId);
              }, 'Taal opgeslagen voor de hele groep')
            }
          />
          {!confirmed ? (
            <span className="checkin-flow-lang-hint muted">(nog niet bevestigd)</span>
          ) : null}
          {state?.can_split ? (
            <button
              type="button"
              className="btn-link checkin-flow-lang-split"
              disabled={busy}
              onClick={() =>
                void run(
                  () => setPlayLanguageMode(departureId, 'per_person'),
                  'Je kunt nu per persoon instellen',
                )
              }
            >
              per persoon instellen ›
            </button>
          ) : null}
        </>
      ) : (
        <>
          <ul className="checkin-flow-lang-people">
            {(state?.people ?? []).map((p) => (
              <li key={p.queue_entry_id} className="checkin-flow-lang-person">
                <span className="checkin-flow-lang-person-label">
                  Persoon {p.person_index}
                </span>
                <LanguageChips
                  value={p.play_language}
                  disabled={busy}
                  compact
                  options={optionsFor(langOptions, experienceId, p.play_language)}
                  onChange={(code) =>
                    void run(
                      () => setEntryPlayLanguage(p.queue_entry_id, code),
                      `Persoon ${p.person_index}: ${code.toUpperCase()}`,
                    )
                  }
                />
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="btn-link checkin-flow-lang-split"
            disabled={busy}
            onClick={() =>
              void run(
                () => setPlayLanguageMode(departureId, 'group'),
                'Weer één taal voor iedereen',
              )
            }
          >
            ‹ terug naar één taal voor iedereen
          </button>
        </>
      )}

      {note ? (
        <div className="checkin-flow-lang-note" role="status">
          {note} ✓
        </div>
      ) : null}
      {error ? (
        <div className="checkin-flow-lang-error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
